import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import ProfileHeader from '../components/ProfileHeader';
import ProfileTaskBox from '../components/ProfileTaskBox';
import ProfileSkillBox from '../components/ProfileSkillBox';
import { styled } from '@mui/material/styles';
import ProfileAvailabilityBox from '../components/ProfileAvailabilityBox';
import ProfileTimeOffBox from '../components/ProfileTimeOffBox';
import ProfileContactBox from '../components/ProfileContactBox';
import ProfileAccountsBox from '../components/ProfileAccountsBox';
import ProfileActivityBox from '../components/ProfileActivityBox';
import { getTasksByUser } from '../redux/ducks/task';
import { useParams } from 'react-router-dom';
import { usePageTitle } from '../utils/brandUtils';
import { getUser } from '../redux/slices/users';
import { getTimeOffSummary } from '../redux/ducks/timeoff';
import { useHasPermission } from '../utils/permissionUtils';
const DashboardContainer = styled(Grid)(({ theme }) => ({
    maxWidth: theme.breakpoints.values.lg,
}));

// Holds an empty slot in the 3-column grid so the large Accounts box keeps its
// position instead of back-filling a hidden box's slot (only relevant on md+).
const GridSpacer = styled(Grid)(({ theme }) => ({
    display: "none",
    [theme.breakpoints.up("md")]: {
        display: "block",
    },
}));

const Dashboard = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const { userId : userIdParam } = useParams();
    const [userId, setUserId] = useState(userIdParam);
    const currentUserId = useSelector(state => state.auth.userId);
    const [user, setUser] = useState();
    const [tasksStatus, setTasksStatus] = useState([]);
    const [tasksPriority, setTasksPriority] = useState([]);
    const [timeOffSummary, setTimeOffSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const canSeeInternal = hasPermission('canSeeInternal');
    const canSeeTimeOff = hasPermission('seeTimeOff');
    const isSelf = userId === useSelector(state => state.auth.userId);
    const timeOffRefresh = useSelector((state) => state.refreshTriggers.timeOff);

    usePageTitle(isSelf ? 'My Profile' : user?.fullName || 'Profile');

    useEffect(() => {
        if (userIdParam) {
            setUserId(userIdParam);
        }
    }, [userIdParam]);

    useEffect(() => {
        if (!userId && currentUserId) {
            setUserId(currentUserId);
        }
    }, [userId, currentUserId]);

    useEffect(() => {
        if (userId) {
            setIsLoading(true);
            dispatch(getUser({userId: (isSelf ? null : userId), callback: (userResponse) => {
                setUser(userResponse);
            }}));
        }
    }, [userId, isSelf, dispatch]);

    useEffect(() => {
        if (!canSeeInternal) {
            setIsLoading(false);
            return;
        }
        if (!userId) return;
        dispatch(getTasksByUser(userId, (tasksResponse) => {
            setTasksStatus(tasksResponse.statusCount || []);
            setTasksPriority(tasksResponse.priorityCount || []);
            if (isSelf || canSeeTimeOff) {
                dispatch(getTimeOffSummary(userId, (timeOffSummaryResponse) => {
                    setTimeOffSummary(timeOffSummaryResponse);
                    setIsLoading(false);
                }));
            } else {
                setIsLoading(false);
            }
        }));
    }, [user, userId, canSeeInternal, canSeeTimeOff, isSelf, timeOffRefresh, dispatch]);

    return (
        <>
            <DashboardContainer container spacing={3}>
                {user && (
                    <>
                        <ProfileHeader user={user} isLoading={isLoading} canSeeInternal={canSeeInternal} />
                        <ProfileContactBox user={user} isLoading={isLoading} canSeeInternal={canSeeInternal} />
                        {(canSeeInternal) && (
                            <ProfileAvailabilityBox user={user} onUserChange={setUser} />
                        )}
                        {canSeeInternal && (
                            <>
                                {user?.skills && (
                                    <ProfileSkillBox userId={userId} skills={user.skills} isLoading={isLoading} />
                                )}
                                <ProfileTaskBox 
                                    key={`priority-${JSON.stringify(tasksPriority)}`}
                                    title="Open Tasks" 
                                    data1={tasksPriority} 
                                    isLoading={isLoading} 
                                />
                                <ProfileTaskBox 
                                    key={`status-${JSON.stringify(tasksStatus)}`}
                                    title="Tasks Status" 
                                    data1={tasksStatus} 
                                    isLoading={isLoading} 
                                />
                                {(isSelf || canSeeTimeOff) ? (
                                    <ProfileTimeOffBox title="Time Off" userId={userId} data={timeOffSummary} isLoading={isLoading} />
                                ) : (
                                    <GridSpacer item md={4} lg={4} />
                                )}
                            </>
                        )}
                        
                        {user?.accounts && (
                            <ProfileAccountsBox accounts={user.accounts} isLoading={isLoading} />
                        )}
                        <ProfileActivityBox userId={userId} canSeeInternal={canSeeInternal} />
                    </>
                )}
            </DashboardContainer>
        </>
    );
};

export default Dashboard;
